import type { ChangeEvent, FC } from "react";

import { RadioContainer } from "@jti/core-components";
import { Card } from "@jti/core-components/components/Card";
import { Tooltip, useTranslations } from "@jti/ui";

import { DeliveryAgeVerificationBlock } from "./DeliveryAgeVerificationBlock";
import type { DeliveryInformationProps } from "./DeliveryInformation.types";
import { getSingleMethodMessageKey } from "./utils";

import styles from "./DeliveryInformation.module.scss";

const DEFAULT_RADIO_NAME = "shippingMethodCode";

const SingleMethod: FC<{ method: DeliveryInformationProps["methods"][number] }> = ({ method }) => {
  const { __ } = useTranslations();
  const messageKey = getSingleMethodMessageKey(method.type);

  return (
    <div className={styles.singleMethod} data-testid="delivery-information-single">
      {method.icon && <img src={method.icon} alt="" className={styles.singleMethodIcon} aria-hidden="true" />}
      <span className={styles.singleMethodText}>{__(messageKey)}</span>
    </div>
  );
};

const MultiMethod: FC<
  Pick<
    DeliveryInformationProps,
    "methods" | "selectedValue" | "onMethodChange" | "isShippingAgeVerificationEnabled" | "isAgeVerified"
  > & { name: string }
> = ({ methods, selectedValue, onMethodChange, isShippingAgeVerificationEnabled, isAgeVerified, name }) => {
  const { __ } = useTranslations();

  const isMethodDisabledForAgeVerification = (method: DeliveryInformationProps["methods"][number]) =>
    Boolean(isShippingAgeVerificationEnabled && method.disableForNotAgeVerified && !isAgeVerified);
  const methodsByAvailability = [...methods].sort(
    (firstMethod, secondMethod) =>
      Number(isMethodDisabledForAgeVerification(firstMethod)) - Number(isMethodDisabledForAgeVerification(secondMethod))
  );

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    onMethodChange(event.target.value);
  };

  return (
    <div className={styles.multiMethod}>
      <fieldset className={styles.methodList}>
        <legend className={styles.question}>
          <span className={styles.questionText}>{__("cart.checkout.delivery.options_description")}</span>
          <Tooltip content={__("cart.checkout.delivery_information.tooltip")} size="small" />
        </legend>
        {methodsByAvailability.map((method) => {
          const trailingIcon = method.icon ? (
            <img src={method.icon} alt="" className={styles.methodIcon} aria-hidden="true" />
          ) : undefined;
          const isDisabled = isMethodDisabledForAgeVerification(method);

          return (
            <RadioContainer
              key={method.value}
              name={name}
              value={method.value}
              checked={selectedValue === method.value}
              onChange={handleChange}
              label={method.label}
              icon={trailingIcon}
              disabled={isDisabled}
              description={isDisabled ? __("cart.checkout.delivery.age_verification.option_description") : undefined}
            />
          );
        })}
      </fieldset>
    </div>
  );
};

export const DeliveryInformation: FC<DeliveryInformationProps> = ({
  methods,
  selectedValue,
  onMethodChange,
  isShippingAgeVerificationEnabled = false,
  isAgeVerified,
  hasCompletedAgeVerification,
  isAgeVerificationError,
  isAgeVerificationPending,
  onVerifyAge,
  name = DEFAULT_RADIO_NAME,
  children,
}) => {
  const { __ } = useTranslations();

  if (methods.length === 0) {
    return null;
  }

  const isSingleMethod = methods.length === 1;
  const hasAgeRestrictedMethod =
    isShippingAgeVerificationEnabled && methods.some((method) => method.disableForNotAgeVerified);
  const shouldShowAgeVerification = hasAgeRestrictedMethod && (!isAgeVerified || hasCompletedAgeVerification);

  return (
    <div data-testid="delivery-information-card">
      <Card title={__("cart.checkout.delivery_information.title")}>
        <div className={styles.content}>
          <div className={styles.deliveryOptions} data-testid="delivery-information-options">
            <div className={styles.section}>
              {isSingleMethod ? (
                <SingleMethod method={methods[0]} />
              ) : (
                <MultiMethod
                  methods={methods}
                  selectedValue={selectedValue}
                  onMethodChange={onMethodChange}
                  isShippingAgeVerificationEnabled={isShippingAgeVerificationEnabled}
                  isAgeVerified={isAgeVerified}
                  name={name}
                />
              )}
            </div>
            {shouldShowAgeVerification ? (
              <div className={styles.section}>
                <DeliveryAgeVerificationBlock
                  isAgeVerified={isAgeVerified}
                  hasCompletedAgeVerification={hasCompletedAgeVerification}
                  isAgeVerificationError={isAgeVerificationError}
                  isAgeVerificationPending={isAgeVerificationPending}
                  onVerifyAge={onVerifyAge}
                />
              </div>
            ) : null}
          </div>
          {children ? <div className={styles.section}>{children}</div> : null}
        </div>
      </Card>
    </div>
  );
};
